import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, BellOff, Check } from 'lucide-react';
import axiosInstance from '../../../utils/axiosConfig';
import { SHADOW } from '../utils/uiTokens';

/**
 * Silenciar avisos de una tarea o de su proyecto completo (por usuario).
 * Silenciar corta los avisos de cambios (estado, fechas, comentarios) y el
 * recordatorio diario; una asignacion directa siempre llega.
 *
 * Props: tareaId, proyectoId, silenciada, proyectoSilenciado, onChanged
 */
const SilenciarControl = ({ tareaId, proyectoId, silenciada, proyectoSilenciado, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const ref = useRef(null);
  const activo = silenciada || proyectoSilenciado;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = async (url, estado) => {
    if (guardando) return;
    setGuardando(true);
    try {
      if (estado) await axiosInstance.delete(url);
      else await axiosInstance.post(url);
      onChanged?.();
    } catch (err) {
      console.error('Error cambiando silencio:', err);
    } finally {
      setGuardando(false);
    }
  };

  const Opcion = ({ label, detalle, marcado, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={guardando}
      className="w-full flex items-start gap-2.5 px-2.5 py-2 rounded-md text-left hover:bg-slate-50 transition-colors disabled:opacity-60"
    >
      <span className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
        marcado ? 'bg-slate-900 border-slate-900' : 'border-slate-300'
      }`}
      >
        {marcado && <Check size={11} strokeWidth={3} className="text-white" />}
      </span>
      <span className="min-w-0">
        <span className="block text-sm text-slate-800">{label}</span>
        <span className="block text-xs text-slate-500 mt-0.5">{detalle}</span>
      </span>
    </button>
  );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={activo ? 'Avisos silenciados' : 'Silenciar avisos'}
        aria-expanded={open}
        className={`p-1.5 rounded transition-colors ${
          activo
            ? 'text-amber-600 bg-amber-50 hover:bg-amber-100'
            : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'
        }`}
      >
        {activo ? <BellOff size={14} /> : <Bell size={14} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            className={`absolute right-0 top-full mt-1.5 w-[290px] bg-white rounded-lg p-1 z-40 ${SHADOW.dropdown}`}
          >
            <p className="px-2.5 pt-2 pb-1 text-xs text-slate-500">
              Si alguien te asigna una tarea, el aviso te llega igual.
            </p>
            <Opcion
              label="Silenciar esta tarea"
              detalle="Sin avisos de sus cambios, comentarios ni vencimiento."
              marcado={silenciada}
              onClick={() => toggle(`/api/tareas/${tareaId}/silenciar`, silenciada)}
            />
            <Opcion
              label="Silenciar todo el proyecto"
              detalle="Lo mismo para todas las tareas del proyecto."
              marcado={proyectoSilenciado}
              onClick={() => toggle(`/api/proyectos/${proyectoId}/silenciar`, proyectoSilenciado)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SilenciarControl;
